import {
	PRIORITY_TYPE,
	ASSIGNEE_TYPE,
	STATUS_TYPE,
	SPRINT_TYPE,
	NUMBER_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	CATEGORY_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { OptimisticFieldSet } from './optimistic-responses/types.tsx';
import type { GroupContextField } from './types.tsx';
import {
	toOptimisticPriority,
	toOptimisticUser,
	toOptimisticStatus,
	toOptimisticSingleSelect,
	toOptimisticSprint,
	toOptimisticNumber,
} from './optimistic-responses/index.tsx';

export const SUPPORTED_FIELD_TYPES: Set<string> = new Set([
	PRIORITY_TYPE,
	ASSIGNEE_TYPE,
	STATUS_TYPE,
	SPRINT_TYPE,
	NUMBER_CF_TYPE,
	STORY_POINT_ESTIMATE_CF_TYPE,
	CATEGORY_TYPE,
]);

export const isCreateIssueSupportedForFieldType = (fieldType?: string | null) =>
	Boolean(fieldType && SUPPORTED_FIELD_TYPES.has(fieldType));

export const convertFieldToStatusGICInput = ({ fieldValue }: GroupContextField) => {
	if (fieldValue === null) {
		return null;
	}

	return { status: { statusId: fieldValue } };
};

export const convertFieldToGICAdditionalFieldsPayload = (
	groupContextFieldInput: GroupContextField,
) => {
	const { fieldType, fieldValue, fieldId } = groupContextFieldInput;
	if (fieldValue === null) {
		return {};
	}

	// Note: excluding status type as it's always shown in GIC
	switch (fieldType) {
		case PRIORITY_TYPE:
		case ASSIGNEE_TYPE:
			return {
				[fieldId]: {
					id: fieldValue,
				},
			};
		case STORY_POINT_ESTIMATE_CF_TYPE:
		case NUMBER_CF_TYPE:
		case SPRINT_TYPE:
			return {
				[fieldId]: {
					id: fieldId,
					value: fieldValue,
				},
			};
		case CATEGORY_TYPE:
			return {
				[fieldId]: {
					id: fieldValue,
					value: fieldValue,
				},
			};
		default:
			return null;
	}
};

export const convertFieldToIssueCreateInput = ({
	fieldType,
	fieldValue,
	fieldId,
}: GroupContextField) => {
	if (fieldValue === null) {
		return null;
	}

	switch (fieldType) {
		case PRIORITY_TYPE:
			return { priority: { priorityId: fieldValue } };
		case ASSIGNEE_TYPE:
			return {
				singleSelectUserPickerFields: [{ fieldId, user: { accountId: fieldValue } }],
			};
		case STATUS_TYPE:
			return { status: { statusId: fieldValue } };
		case STORY_POINT_ESTIMATE_CF_TYPE:
		case NUMBER_CF_TYPE:
			return { numberFields: [{ fieldId, value: fieldValue }] };
		case CATEGORY_TYPE:
			return {
				singleSelectFields: [
					{
						fieldId,
						option: { id: fieldValue, optionId: fieldValue },
					},
				],
			};
		case SPRINT_TYPE:
			return { sprintsField: { fieldId, sprints: [{ sprintId: fieldValue }] } };
		default:
			return null;
	}
};

export const toOptimisticField = (
	issueId: string,
	{ fieldId, fieldType, optimisticResponse }: GroupContextField,
): OptimisticFieldSet => {
	switch (optimisticResponse?.type) {
		case PRIORITY_TYPE:
			return toOptimisticPriority({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case ASSIGNEE_TYPE:
			return toOptimisticUser({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case STATUS_TYPE:
			return toOptimisticStatus({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case CATEGORY_TYPE:
			return toOptimisticSingleSelect({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case SPRINT_TYPE:
			return toOptimisticSprint({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		case NUMBER_CF_TYPE:
			return toOptimisticNumber({
				issueId,
				fieldId,
				fieldType,
				value: optimisticResponse,
			});
		default:
			return null;
	}
};
